import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Box, Drawer, IconButton, Tooltip, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useChatWebSocket from './websocket';
import { Toaster } from 'react-hot-toast';
import { API_BASE_URL } from './api';
// Remove api imports

// Import primary goals data for reference
const primaryGoals = [
  { id: 'info', label: 'Richiesta informazioni' },
  { id: 'email', label: 'Rispondere ad una mail' },
  { id: 'lead', label: 'Impostare un primo contatto con un lead' },
  { id: 'social', label: 'Creare un contenuto social' },
  { id: 'summary', label: 'Riassumere una comunicazione' }
];

// Import components
import Header from './components/Header';
import ChatPanel from './components/ChatPanel';
import SearchPanel from './components/SearchPanel';
import PromptStructureDialog from './components/PromptStructureDialog';
import ChatHistoryPanel from './components/ChatHistoryPanel';
import KnowledgePanel from './components/KnowledgePanel';
// Remove EmailPanel import
// Add any utils imports from the correct location
import { formatPrice, truncateText } from "../../utils/utils";
import { Menu, ChevronLeft, ChevronRight } from 'lucide-react';
import CarDetailModal from './components/CarDetailModal'; // Import the modal component

// Remove the useMessageInput hook
// const useMessageInput = () => {
//   const [message, setMessage] = useState('');
  
//   const handleMessageChange = useCallback((newMessage) => {
//     setMessage(newMessage);
//   }, []);
  
//   return { message, setMessage: handleMessageChange };
// };

const InternalAssistant = memo(({ open, onClose, darkMode }) => {
  // Remove message state from here
  // const { message, setMessage } = useMessageInput();
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const [showPromptStructure, setShowPromptStructure] = useState(false);
  const [searchTab, setSearchTab] = useState('cars');
  // Add knowledge panel state
  const [isKnowledgePanelOpen, setIsKnowledgePanelOpen] = useState(false);
  
  // Remove email-related state variables
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailPanel, setShowEmailPanel] = useState(false);

  // Chat history state
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  
  // Search states
  const [carSearchTerm, setCarSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    min_price: '',
    max_price: '',
    min_year: '',
    max_year: '',
    min_km: '',
    max_km: '',
    fuel: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    models: [],
    fuel_types: [],
    price_range: { min_price: 0, max_price: 200000 },
    year_range: { min_year: 1990, max_year: new Date().getFullYear() }
  });

  const chatContainerRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  const {
    messages,
    setMessages,
    isLoading,
    handleSend,
    error,
    initializeWebSocket,
    cleanup,
    websocket,
    chatId,
    setChatId,
    uploadedFiles,
    fileUploadProgress,
    handleFileUpload,
    handleDeleteFile
  } = useChatWebSocket();

  // Search debounce timeouts
  const carSearchTimeout = useRef(null);
  const clientSearchTimeout = useRef(null);

  // Add to existing state declarations
  const [systemPrompt, setSystemPrompt] = useState('');
  const [selectedCars, setSelectedCars] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  // State for car detail modal
  const [selectedCarDetail, setSelectedCarDetail] = useState(null);

  useEffect(() => {
    if (open) {
      initializeWebSocket();
    }
    return cleanup;
  }, [open, initializeWebSocket, cleanup]);

  // When creating a new chat, update the history in localStorage
  const updateChatHistory = useCallback((chatId, operation = 'add') => {
    try {
      // Get existing history
      const existingHistory = JSON.parse(localStorage.getItem('aitomotivelab_chat_history') || '[]');
      
      if (operation === 'add') {
        // Check if chat already exists
        const existingChat = existingHistory.find(chat => chat.id === chatId);
        
        if (!existingChat) {
          // Add new chat to history
          const newChat = {
            id: chatId,
            title: `Chat ${existingHistory.length + 1}`,
            lastMessage: 'New conversation',
            date: new Date().toISOString(),
            messages: 0
          };
          
          const updatedHistory = [newChat, ...existingHistory];
          localStorage.setItem('aitomotivelab_chat_history', JSON.stringify(updatedHistory));
        }
      } else if (operation === 'update' && messages.length > 0) {
        // Update existing chat
        const lastMessage = messages[messages.length - 1];
        const lastUserMessage = [...messages].reverse().find(msg => msg.sender === 'user');
        
        const updatedHistory = existingHistory.map(chat => {
          if (chat.id === chatId) {
            return {
              ...chat,
              lastMessage: lastMessage.sender === 'server' 
                ? lastMessage.text.substring(0, 60) + (lastMessage.text.length > 60 ? '...' : '') 
                : chat.lastMessage,
              date: new Date().toISOString(),
              messages: messages.filter(msg => msg.sender === 'server').length
            };
          }
          return chat;
        });
        
        localStorage.setItem('aitomotivelab_chat_history', JSON.stringify(updatedHistory));
        
        // Request fresh chat data from the server
        if (websocket && websocket.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify({
            type: 'fetch_user_chats',
            page: 1,
            page_size: 10
          }));
        }
      }
    } catch (error) {
      console.error('Error updating chat history:', error);
    }
  }, [messages]);

  // Handle car search
  useEffect(() => {
    if (carSearchTimeout.current) {
      clearTimeout(carSearchTimeout.current);
    }

    if (carSearchTerm.trim()) {
      setSearchLoading(true);
      carSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await searchCars(carSearchTerm, filters);
          setSearchResults(results);
        } catch (error) {
          console.error('Error searching cars:', error);
        } finally {
          setSearchLoading(false);
        }
      }, 500);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (carSearchTimeout.current) {
        clearTimeout(carSearchTimeout.current);
      }
    };
  }, [carSearchTerm, filters]);

  // Handle client search
  useEffect(() => {
    if (clientSearchTimeout.current) {
      clearTimeout(clientSearchTimeout.current);
    }

    if (clientSearchTerm.trim()) {
      setClientSearchLoading(true);
      clientSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await searchClients(clientSearchTerm);
          setClientSearchResults(results);
        } catch (error) {
          console.error('Error searching clients:', error);
        } finally {
          setClientSearchLoading(false);
        }
      }, 500);
    } else {
      setClientSearchResults([]);
    }

    return () => {
      if (clientSearchTimeout.current) {
        clearTimeout(clientSearchTimeout.current);
      }
    };
  }, [clientSearchTerm]);

  // Update chat history when messages change
  useEffect(() => {
    if (chatId && messages.length > 0) {
      updateChatHistory(chatId, 'update');
    }
  }, [messages, chatId, updateChatHistory]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCarKnowledge = (car) => {
    setSelectedCars(prev => [...prev, car]);
    setSearchResults(prevResults => 
      prevResults.map(result => 
        result.vehicleid === car.vehicleid ? { ...result, highlighted: true } : result
      )
    );
  };

  const handleAddClientKnowledge = (client) => {
    setSelectedClients(prev => [...prev, client]);
    setClientSearchResults(prevResults => 
      prevResults.map(result => 
        result.id === client.id ? { ...result, highlighted: true } : result
      )
    );
  };

  const handleRemoveCar = (carToRemove) => {
    //console.log('Removing car:', carToRemove);
    //console.log('Current selected cars:', selectedCars);
    
    setSelectedCars(prevCars => {
      const updatedCars = prevCars.filter(car => car.vehicleid !== carToRemove.vehicleid);
      //console.log('Updated selected cars:', updatedCars);
      return updatedCars;
    });
    
    setSearchResults(prevResults => 
      prevResults.map(result => 
        result.vehicleid === carToRemove.vehicleid ? { ...result, highlighted: false } : result
      )
    );
  };

  const handleRemoveClient = (client) => {
    setSelectedClients(prev => prev.filter(c => c.id !== client.id));
    setClientSearchResults(prevResults => 
      prevResults.map(result => 
        result.id === client.id ? { ...result, highlighted: false } : result
      )
    );
  };

  // Handler to open the car detail modal
  const handleShowCarDetail = useCallback((car) => {
    //console.log("Showing details for car:", car);
    
    // Add validation to ensure we have a valid car object with required fields
    if (!car || !car.vehicleid) {
      console.error("Cannot show car details: Missing vehicleid", car);
      return;
    }
    
    // Make a clean copy of the car object to avoid reference issues
    const carDetails = JSON.parse(JSON.stringify(car));
    
    // Ensure model exists - extract from description if not present
    if (!carDetails.model && carDetails.description) {
      const parts = carDetails.description.toLowerCase().split(' ');
      if (parts.length > 1) {
        // Typically the second word in description is the model
        carDetails.model = parts[1];
        //console.log("Extracted model from description:", carDetails.model);
      }
    }
    
    // Create an imagesUrl array from urlmainimage if it exists
    if (carDetails.urlmainimage && !carDetails.imagesUrl) {
      carDetails.imagesUrl = [carDetails.urlmainimage];
      //console.log("Created imagesUrl array from urlmainimage:", carDetails.imagesUrl);
    }
    
    // If there are any imagesUrl available from the car API response, use those
    if (carDetails.imagesUrl && Array.isArray(carDetails.imagesUrl)) {
      //console.log(`Car has ${carDetails.imagesUrl.length} images`);
      //console.log("First few image URLs:", carDetails.imagesUrl.slice(0, 3));
    } else if (!carDetails.imagesUrl) {
      console.warn("Car has no images array:", carDetails);
    }
    
    // Check for accessories and optional features 
    if (car.accessories) {
      //console.log("Car has accessories:", car.accessories);
    } else if (car.standardAccessories) {
      //console.log("Car has standard accessories:", car.standardAccessories);
    } else if (car.standardFeatures) {
      //console.log("Car has standard features:", car.standardFeatures);
    } else {
      // Add placeholder accessories if none exist
      // This is just for demonstration - remove in production
      carDetails.accessories = [
        "Climatizzatore automatico",
        "Cerchi in lega",
        "Sensori di parcheggio",
        "Bluetooth",
        "Sistema di navigazione",
        "Controllo automatico clima",
        "Sedili riscaldati",
        "Vetri elettrici",
        "Cruise control",
        "Specchietti elettrici"
      ];
      //console.log("Added placeholder accessories for demonstration");
    }
    
    // Check for optional features
    if (car.optionals) {
      //console.log("Car has optionals:", car.optionals);
    } else if (car.optionalAccessories) {
      //console.log("Car has optional accessories:", car.optionalAccessories);
    } else if (car.optionalFeatures) {
      //console.log("Car has optional features:", car.optionalFeatures);
    } else {
      // Add placeholder optionals if none exist
      // This is just for demonstration - remove in production
      carDetails.optionals = [
        "Tetto panoramico",
        "Sistema audio premium",
        "Head-up display",
        "Pacchetto sportivo",
        "Ricarica wireless",
        "Telecamera 360°",
        "Assistente di guida autonoma",
        "Fari LED adattivi"
      ];
      //console.log("Added placeholder optionals for demonstration");
    }
    
    setSelectedCarDetail(carDetails);
  }, []);

  // Handler to close the car detail modal
  const handleCloseCarDetail = useCallback(() => {
    setSelectedCarDetail(null);
  }, []);

  // Update handleSubmit to handle file uploads
  const handleSubmit = useCallback(async (newMessage) => {
    if (!newMessage.trim()) return;
    await handleSend(newMessage);
  }, [handleSend]);

  // Ensure search is open when needed
  const ensureSearchIsOpen = () => {
    if (!isSearchOpen) {
      setIsSearchOpen(true);
    }
  };

  // Update this handler
  const handleGoalsSelected = (promptData) => {
    // Check if we have a generated prompt text
    if (promptData.promptText) {
      // Generate a message showing what the user selected (for display in the UI)
      const primaryGoalLabel = primaryGoals.find(goal => goal.id === promptData.primaryGoal)?.label || '';
      const userSelectionMessage = `Ho selezionato l'obiettivo: ${primaryGoalLabel} - ${promptData.secondaryGoal}`;
      
      // Log the structured prompt data for debugging
      //console.log('Sending structured prompt data:', {
      //  userMessage: userSelectionMessage,
      //  promptData: promptData
      //});
      
      // Send the structured prompt with the generated text through the existing handleSend function
      // Using the FULL prompt text instead of just the selection message
      handleSend(promptData.promptText, null, null, 'structured_prompt', promptData);
    } else {
      // Fallback to the original behavior if no prompt text is available
      //console.log('Warning: No prompt text available in promptData');
      handleSend('', null, null, 'structured_prompt', promptData);
    }
    
    setShowPromptStructure(false);
  };

  const handleResetConversation = useCallback(() => {
    // Reset UI state
    setMessages([]);
    setSearchResults([]);
    setClientSearchResults([]);
    setCarSearchTerm('');
    setClientSearchTerm('');
    setFilters({
      brand: '',
      model: '',
      fuel: '',
      min_price: '',
      max_price: '',
      min_year: '',
      max_year: '',
      min_km: '',
      max_km: '',
    });
    setSelectedCars([]);
    setSelectedClients([]);
    setSystemPrompt('');
    
    const previousChatId = chatId;

    // Clear stored chat id before creating a new one
    localStorage.removeItem('aitomotivelab_personalData_chat_id');
    
    // If we are in an existing chat, ask the backend to leave it first
    const sendCreateChat = () => {
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        try {
          websocket.send(JSON.stringify({ type: 'create_chat' }));
        } catch (e) {
          if (initializeWebSocket) {
            initializeWebSocket();
          }
        }
      } else {
    if (cleanup) {
      cleanup();
    }
    if (initializeWebSocket) {
      initializeWebSocket();
    }
      }
    };

    if (previousChatId && websocket && websocket.readyState === WebSocket.OPEN) {
      try {
        websocket.send(JSON.stringify({ type: 'leave_chat', chat_id: previousChatId }));
      } catch (e) {
        console.error('Error sending leave_chat:', e);
      }
      // Optimistically reset client-side chat id
      if (setChatId) {
        setChatId(null);
      }
      setTimeout(sendCreateChat, 200);
    } else {
      sendCreateChat();
    }
  }, [cleanup, initializeWebSocket, websocket, chatId, setMessages, setSearchResults, setClientSearchResults, setCarSearchTerm, setClientSearchTerm, setFilters, setSelectedCars, setSelectedClients, setSystemPrompt, setChatId]);

  // Create a new chat
  const handleCreateNewChat = useCallback(() => {
    handleResetConversation();
    // Chat ID will be created by the websocket and then updated in history
  }, [handleResetConversation]);

  // Select an existing chat
  const handleSelectChat = useCallback((selectedChatId) => {
    //console.log(`Selecting chat with ID: ${selectedChatId}`);
    
    // Clean the chat ID - extract just the part before the underscore if it exists
    // This fixes issues where the backend expects a different format
    const cleanedChatId = selectedChatId.includes('_') 
      ? selectedChatId.split('_')[0] 
      : selectedChatId;
    
    //console.log(`Using cleaned chat ID: ${cleanedChatId}`);
    
    // Reset current chat
    setMessages([]);
    
    // Set the new chat ID in localStorage before reinitializing
    localStorage.setItem('aitomotivelab_personalData_chat_id', cleanedChatId);
    
    // Additional cleanup to ensure we're starting fresh
    setSelectedCars([]);
    setSelectedClients([]);
    setSystemPrompt('');
    
    // Update the websocket reference directly instead of causing a full reconnect
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      //console.log(`Requesting to join chat: ${cleanedChatId}`);
      const message = {
        type: 'join_chat',
        chat_id: cleanedChatId
      };
      
      try {
        websocket.send(JSON.stringify(message));
        //console.log('join_chat message sent successfully');
      } catch (err) {
        console.error('Error sending join_chat message:', err);
        // If message send fails, try reconnecting
        if (initializeWebSocket) {
          //console.log('Attempting to reconnect WebSocket');
          initializeWebSocket();
        }
      }
      
      // Close history panel on mobile after selection
      if (isMobile) {
        setIsHistoryPanelOpen(false);
      }
    } else {
      // If no active connection, reinitialize
      //console.log('WebSocket not connected, initializing new connection');
      if (cleanup) {
        cleanup();
      }
      if (initializeWebSocket) {
        initializeWebSocket();
      }
    }
  }, [cleanup, initializeWebSocket, setMessages, isMobile, websocket]);

  // Toggle history panel
  const toggleHistoryPanel = () => {
    setIsHistoryPanelOpen(!isHistoryPanelOpen);
  };

  // Add message listener for prompt structure dialog
  useEffect(() => {
    if (open) {
      const handleMessage = (event) => {
        if (event.data) {
          if (event.data.type === 'openPromptStructure') {
            setShowPromptStructure(true);
          } else if (event.data.type === 'openSearchPanel') {
            // Open search panel and set the active tab
            setIsSearchOpen(true);
            if (isMobile) {
              setMobileSearchVisible(true);
            }
            if (event.data.tab) {
              setSearchTab(event.data.tab);
            }
          } else if (event.data.type === 'internalAssistantMessage' && event.data.message) {
            // Handle messages sent from components like the car detail modal
            //console.log("Received message from component:", event.data.message);
            
            // Send the message through the websocket
            if (handleSend) {
              handleSend(event.data.message);
            }
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [open, isMobile, handleSend]);

  // Load Chart.js library dynamically on mount
  useEffect(() => {
    if (open) {
      // Check if Chart.js is already loaded
      if (!window.Chart && !document.getElementById('chartjs-script')) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
        script.id = 'chartjs-script';
        script.async = true;
        document.head.appendChild(script);

        // Cleanup function
        return () => {
          const chartScript = document.getElementById('chartjs-script');
          if (chartScript) {
            document.head.removeChild(chartScript);
          }
        };
      }
    }
  }, [open]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isFullscreen ? '100%' : { 
              xs: '100%', 
              sm: isSearchOpen ? '1200px' : '800px' // Increased width to accommodate knowledge panel
            },
            height: { 
              xs: 'calc(100% - 64px)',
              md: 'calc(100% - 80px)'
            },
            top: { xs: '64px', md: '80px' },
            bgcolor: darkMode ? '#1E1E1E' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#000000',
            transition: 'width 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexGrow: 1,
          overflow: 'hidden',
          flexDirection: 'row',
          maxHeight: '100%',
          position: 'relative'
        }}>
          {/* Chat History Panel - Only visible when isHistoryPanelOpen is true */}
          {isHistoryPanelOpen && (
            <Box sx={{ 
              display: { xs: 'block', md: 'block' },
              height: '100%',
              flexShrink: 0,
              width: { xs: '100%', md: '280px' },
              position: { xs: 'absolute', md: 'relative' },
              zIndex: { xs: 1300, md: 'auto' },
              left: 0,
              top: 0,
              bottom: 0,
              bgcolor: darkMode ? '#1E1E1E' : '#FFFFFF',
            }}>
              <ChatHistoryPanel 
                darkMode={darkMode}
                onSelectChat={handleSelectChat}
                onCreateNewChat={handleCreateNewChat}
                currentChatId={chatId}
                isLoading={isLoading && !messages.length}
                websocket={websocket}
                connectionError={error}
                onReconnect={() => initializeWebSocket()}
              />
            </Box>
          )}

          {/* Main Chat Area */}
          <Box sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
            height: '100%'
          }}>
            {/* Search Panel */}
            {(isSearchOpen || mobileSearchVisible) && (
              <Box sx={{ 
                width: isFullscreen ? '30%' : { xs: '100%', sm: '350px' },
                height: '100%',
                overflow: 'auto',
                borderRight: 1,
                borderColor: 'divider',
                ...(isMobile ? {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2000,
                  bgcolor: darkMode ? '#1E1E1E' : '#FFFFFF',
                } : {
                  position: 'relative',
                  display: isFullscreen ? 'block' : { xs: 'none', sm: 'block' },
                })
              }}>
                <SearchPanel 
                  searchTab={searchTab}
                  setSearchTab={setSearchTab}
                  carSearchTerm={carSearchTerm}
                  setCarSearchTerm={setCarSearchTerm}
                  clientSearchTerm={clientSearchTerm}
                  setClientSearchTerm={setClientSearchTerm}
                  searchResults={searchResults}
                  clientSearchResults={clientSearchResults}
                  searchLoading={searchLoading}
                  clientSearchLoading={clientSearchLoading}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  filters={filters}
                  filterOptions={filterOptions}
                  handleFilterChange={handleFilterChange}
                  handleAddCarKnowledge={handleAddCarKnowledge}
                  handleAddClientKnowledge={handleAddClientKnowledge}
                  isFullscreen={isFullscreen}
                  darkMode={darkMode}
                  setSearchResults={setSearchResults}
                  setClientSearchResults={setClientSearchResults}
                  setSearchLoading={setSearchLoading}
                  setClientSearchLoading={setClientSearchLoading}
                  setFilters={setFilters}
                  setFilterOptions={setFilterOptions}
                  isMobile={isMobile}
                  onClose={() => {
                    setIsSearchOpen(false);
                    setMobileSearchVisible(false);
                  }}
                  showCloseButton={true}
                />
              </Box>
            )}

            {/* Chat Panel */}
            <Box sx={{ 
              width: { 
                xs: '100%', 
                sm: isSearchOpen && isKnowledgePanelOpen 
                  ? 'calc(100% - 350px - 300px)' 
                  : isSearchOpen || isKnowledgePanelOpen
                    ? 'calc(100% - 300px)'
                    : '100%'
              },
              display: isMobile && mobileSearchVisible ? 'none' : 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              height: '100%',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Header 
                isMobile={isMobile}
                setMobileSearchVisible={setMobileSearchVisible}
                onClose={onClose}
                setIsSearchOpen={setIsSearchOpen}
                darkMode={darkMode}
                onResetConversation={handleResetConversation}
                onToggleHistoryPanel={toggleHistoryPanel}
                isHistoryPanelOpen={isHistoryPanelOpen}
                isSearchOpen={isSearchOpen}
                isKnowledgePanelOpen={isKnowledgePanelOpen}
                setIsKnowledgePanelOpen={setIsKnowledgePanelOpen}
              />
              
              {/* Connection status indicator */}
              {error && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 65, 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    bgcolor: 'error.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '0 0 8px 8px',
                    fontSize: '0.8rem',
                    zIndex: 100,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: 2,
                    maxWidth: '90%',
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {error}
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => initializeWebSocket()}
                      sx={{ 
                        ml: 2, 
                        color: 'white', 
                        borderColor: 'white',
                        minWidth: 'auto',
                        px: 1,
                        '&:hover': { 
                          borderColor: 'white', 
                          bgcolor: 'rgba(255,255,255,0.1)' 
                        }
                      }}
                    >
                      Reconnect
                    </Button>
                  </Box>
                </Box>
              )}
              
              {/* History toggle button for mobile (floats over content) */}
              {isMobile && !isHistoryPanelOpen && (
                <Tooltip title="Chat History">
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 70,
                      left: 8,
                      bgcolor: darkMode ? 'rgba(60,60,60,0.8)' : 'rgba(240,240,240,0.8)',
                      zIndex: 100,
                      boxShadow: 2,
                      '&:hover': {
                        bgcolor: darkMode ? 'rgba(80,80,80,0.9)' : 'rgba(220,220,220,0.9)',
                      }
                    }}
                    onClick={toggleHistoryPanel}
                  >
                    <Menu size={20} />
                  </IconButton>
                </Tooltip>
              )}
              
              <ChatPanel 
                messages={messages}
                isLoading={isLoading}
                error={error}
                handleSubmit={handleSubmit}
                handleFileUpload={handleFileUpload}
                darkMode={darkMode}
                chatContainerRef={chatContainerRef}
                isMobile={isMobile}
                handleReconnect={() => initializeWebSocket()}
                setShowPromptStructure={setShowPromptStructure}
                onShowCarDetail={handleShowCarDetail}
                fileUploadProgress={fileUploadProgress}
                uploadedFiles={uploadedFiles}
                handleDeleteFile={handleDeleteFile}
              />
            </Box>

            {/* Knowledge Panel - Right Side */}
            {isKnowledgePanelOpen && (
              <Box sx={{ 
                width: { xs: '100%', sm: '300px' },
                height: '100%',
                overflow: 'auto',
                borderLeft: 1,
                borderColor: 'divider',
                flexShrink: 0,
                display: { xs: isMobile ? 'none' : 'block', sm: 'block' },
                bgcolor: darkMode ? '#171717' : '#FCFCFC',
                padding: 2
              }}>
                <KnowledgePanel 
                  files={uploadedFiles}
                  onDeleteFile={handleDeleteFile}
                  isLoading={isLoading}
                  darkMode={darkMode}
                  onUploadFile={handleFileUpload}
                  uploadProgress={fileUploadProgress}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
      
      <PromptStructureDialog 
        open={showPromptStructure} 
        onClose={() => setShowPromptStructure(false)}
        onGoalsSelected={handleGoalsSelected}
        selectedCars={selectedCars}
        selectedClients={selectedClients}
        onRemoveCar={handleRemoveCar}
        onRemoveClient={handleRemoveClient}
      />

      {/* Render the Car Detail Modal */}
      <CarDetailModal 
        car={selectedCarDetail}
        open={Boolean(selectedCarDetail)}
        onClose={handleCloseCarDetail}
        darkMode={darkMode}
      />

      <Toaster position="top-right" />
    </>
  );
});

export default memo(InternalAssistant);
